import * as React from "react"
import { cn } from "@/lib/utils"

interface CircularProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  size?: number
  strokeWidth?: number
  showValue?: boolean
  scoreType?: 'higher-better' | 'lower-better' | 'moderate-better'
  rawScore?: number // Raw score (1-5) for wellness color coding
}

const CircularProgress = React.forwardRef<HTMLDivElement, CircularProgressProps>(
  ({ className, value = 0, size = 64, strokeWidth = 4, showValue = true, scoreType = 'higher-better', rawScore, ...props }, ref) => {
    const normalizedValue = Math.min(Math.max(value, 0), 100)
    const radius = (size - strokeWidth) / 2
    const circumference = radius * 2 * Math.PI
    const strokeDashoffset = circumference - (normalizedValue / 100) * circumference

    const getWellnessColorClass = (score: number, type: string) => {
      if (type === 'higher-better') {
        if (score >= 4) return "stroke-green-500"
        if (score === 3) return "stroke-yellow-500"
        return "stroke-red-500"
      } else if (type === 'lower-better') {
        if (score <= 2) return "stroke-green-500"
        if (score === 3) return "stroke-yellow-500"
        return "stroke-red-500"
      } else if (type === 'moderate-better') {
        if (score === 3) return "stroke-green-500"
        if (score === 2 || score === 4) return "stroke-yellow-500"
        return "stroke-red-500"
      } else {
        // Default fallback for non-wellness usage (percentage based)
        if (score >= 70) return "stroke-green-500"
        if (score >= 40) return "stroke-yellow-500"
        return "stroke-red-500"
      }
    }

    return (
      <div
        ref={ref}
        className={cn("relative inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
        {...props}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            className="opacity-20"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className={cn("transition-all duration-300 ease-in-out", getWellnessColorClass(rawScore !== undefined ? rawScore : normalizedValue, scoreType || 'percentage'))}
          />
        </svg>
        {showValue && (
          <span className="absolute text-sm font-semibold text-foreground">
            {Math.round(normalizedValue)}%
          </span>
        )}
      </div>
    )
  }
)
CircularProgress.displayName = "CircularProgress"

export { CircularProgress }